import {
  computeStandings,
  playerHistory,
  type ResultCode,
  type Score,
  type StandingRow,
  type Tournament,
} from '@ctd/core';

export interface CrossCell {
  /** Short text, e.g. "4w1", "2b½", "bye ½", "–" (not paired). */
  readonly text: string;
  /** Longer description for screen readers and tooltips. */
  readonly title: string;
}

export interface CrossRow {
  readonly rank: string;
  readonly pairingNumber: number;
  readonly name: string;
  readonly rating: number;
  readonly score: Score;
  readonly cells: readonly CrossCell[];
}

const SYMBOL: Record<ResultCode, string> = {
  '1': '1',
  '=': '½',
  '0': '0',
  W: '1',
  D: '½',
  L: '0',
  '+': '+',
  '-': '−',
  U: '1',
  F: '1',
  H: '½',
  Z: '0',
  '*': '',
};

const WORDS: Record<ResultCode, string> = {
  '1': 'won',
  '=': 'drew',
  '0': 'lost',
  W: 'won (unrated)',
  D: 'drew (unrated)',
  L: 'lost (unrated)',
  '+': 'won by forfeit',
  '-': 'lost by forfeit',
  U: 'pairing-allocated bye',
  F: 'full-point bye',
  H: 'half-point bye',
  Z: 'zero-point bye',
  '*': 'no result yet',
};

/**
 * The crosstable after a round: players in standings order, one cell per round
 * giving the opponent's place in this table, the colour and the result.
 */
export function buildCrosstable(
  t: Tournament,
  upToRound = t.rounds.length,
  standings: readonly StandingRow[] = computeStandings(t, { upToRound }),
): CrossRow[] {
  const position = new Map<number, number>();
  standings.forEach((r, i) => position.set(r.pairingNumber, i + 1));
  const byNumber = new Map(t.players.map((p) => [p.pairingNumber, p]));
  return standings.map((row) => {
    const p = byNumber.get(row.pairingNumber);
    const history = p === undefined ? [] : playerHistory(t, p, upToRound);
    const cells = history.map((e, i): CrossCell => {
      const round = i + 1;
      if (e.opponent === null) {
        const pts = SYMBOL[e.result];
        return {
          text: e.result === 'Z' ? '–' : `bye ${pts}`,
          title: `Round ${round}: ${WORDS[e.result]}`,
        };
      }
      const opp = byNumber.get(e.opponent);
      const pos = position.get(e.opponent) ?? e.opponent;
      const colour = e.colour === 'b' ? 'b' : 'w';
      return {
        text: `${pos}${colour}${SYMBOL[e.result]}`,
        title: `Round ${round}: ${WORDS[e.result]} with ${colour === 'w' ? 'white' : 'black'} against ${
          opp?.name ?? `#${e.opponent}`
        } (place ${pos})`,
      };
    });
    return {
      rank: row.rankLabel,
      pairingNumber: row.pairingNumber,
      name: row.name,
      rating: row.rating,
      score: row.score,
      cells,
    };
  });
}
